// Client-side mirror of the schema-definition meta-schema (doc-74).
//
// The server publishes the formal contract at GET /api/schemas/meta-schema
// (JSON Schema draft 2020-12 + additionalChecks, derived from the save-path
// normalizer). This module validates a parsed definition against THAT
// document — patterns, type enums, limits, and model whitelist are read from
// the fetched doc, so the editor's verdict and the published contract can't
// drift. Errors carry a structured `path` (array of keys/indices) the editor
// resolves to an inline marker position.

const FALLBACK = {
  keyPattern: '^[a-z][a-z0-9_]*$',
  fieldTypes: ['string', 'number', 'date', 'boolean', 'enum', 'array', 'object'],
  itemFieldTypes: ['string', 'number', 'date', 'boolean', 'enum'],
  maxFields: 60,
  maxItemFields: 40,
  models: null, // null = don't check
  // No fallback for maxUnionProperties on purpose — see constrainedOutputStatus.
};

// Pull the constraint set out of the meta-schema response (tolerates a
// missing/partial doc by falling back to the shipped constants).
export function constraintsFrom(metaDoc) {
  const schema = metaDoc?.schema || {};
  return {
    keyPattern: schema.$defs?.field?.properties?.key?.pattern || FALLBACK.keyPattern,
    fieldTypes: schema.$defs?.field?.properties?.type?.enum || FALLBACK.fieldTypes,
    itemFieldTypes: schema.$defs?.itemField?.properties?.type?.enum || FALLBACK.itemFieldTypes,
    maxFields: metaDoc?.limits?.maxFields || FALLBACK.maxFields,
    maxItemFields: metaDoc?.limits?.maxItemFields || FALLBACK.maxItemFields,
    models: schema.properties?.model?.enum || FALLBACK.models,
    // Advisory-only and server-owned: null when the server didn't publish it,
    // which makes the editor say NOTHING about constrained runs rather than
    // guess with a stale number.
    maxUnionProperties: Number.isInteger(metaDoc?.limits?.maxUnionProperties)
      ? metaDoc.limits.maxUnionProperties
      : null,
  };
}

const isContainer = (type) => type === 'array' || type === 'object';
const labelFor = (path) => path.reduce(
  (acc, seg) => (typeof seg === 'number' ? `${acc}[${seg}]` : (acc ? `${acc}.${seg}` : seg)),
  ''
);

// Mirrors what normalizeSchemaDefinition rejects (a 400 on save) — nothing
// more: keys the server coerces or ignores are not errors here.
export function validateDefinition(parsed, metaDoc) {
  const errors = [];
  const push = (path, message) => errors.push({ path, label: labelFor(path), message });

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return [{ path: [], label: '', message: 'The top level must be a JSON object.' }];
  }
  const c = constraintsFrom(metaDoc);
  const keyRe = new RegExp(c.keyPattern);

  if (typeof parsed.name !== 'string' || !parsed.name.trim()) {
    push(['name'], 'Schema name is required (a non-empty string).');
  }

  const validateOptions = (f, path) => {
    const options = (Array.isArray(f.options) ? f.options : [])
      .map((o) => String(o).trim()).filter(Boolean);
    if (options.length === 0) {
      push([...path, 'options'], 'Choice fields need at least one option.');
    }
  };

  const validateFieldCore = (f, path, { item }) => {
    const key = typeof f?.key === 'string' ? f.key.trim() : '';
    if (typeof f !== 'object' || f === null || Array.isArray(f)) {
      push(path, 'Each field must be a JSON object.');
      return null;
    }
    if (!keyRe.test(key)) {
      push([...path, 'key'], 'Key must be snake_case (letters, digits, underscores, starting with a letter).');
    }
    const types = item ? c.itemFieldTypes : c.fieldTypes;
    const type = f.type === undefined ? 'string' : f.type;
    if (!types.includes(type)) {
      push(
        [...path, 'type'],
        item && isContainer(type)
          ? 'Item fields are scalar only — no nesting.'
          : `Unknown type ${JSON.stringify(type)} — one of: ${types.join(', ')}.`
      );
    } else if (type === 'enum') {
      validateOptions(f, path);
    }
    return key;
  };

  if (!Array.isArray(parsed.fields) || parsed.fields.length === 0) {
    push(['fields'], 'At least one field is required.');
  } else if (parsed.fields.length > c.maxFields) {
    push(['fields'], `Too many fields (max ${c.maxFields}).`);
  } else {
    const seen = new Set();
    parsed.fields.forEach((f, i) => {
      const path = ['fields', i];
      const key = validateFieldCore(f, path, { item: false });
      if (key) {
        if (seen.has(key)) push([...path, 'key'], `Duplicate field key "${key}".`);
        seen.add(key);
      }
      if (!f || typeof f !== 'object') return;
      // itemFields on a non-container is silently dropped on save — only
      // validated where the server validates it.
      if (isContainer(f.type) && Array.isArray(f.itemFields) && f.itemFields.length) {
        if (f.itemFields.length > c.maxItemFields) {
          push([...path, 'itemFields'], `Too many item fields (max ${c.maxItemFields}).`);
          return;
        }
        const seenItems = new Set();
        f.itemFields.forEach((sf, j) => {
          const itemPath = [...path, 'itemFields', j];
          const itemKey = validateFieldCore(sf, itemPath, { item: true });
          if (itemKey) {
            if (seenItems.has(itemKey)) push([...itemPath, 'key'], `Duplicate item field key "${itemKey}".`);
            seenItems.add(itemKey);
          }
        });
      }
    });
  }

  if (parsed.model !== undefined && parsed.model !== null && String(parsed.model).trim()
    && c.models && !c.models.includes(String(parsed.model).trim())) {
    push(['model'], `Unknown model ${JSON.stringify(parsed.model)} — one of: ${c.models.join(', ')}.`);
  }

  return errors;
}

// Will this definition actually run with CONSTRAINED structured outputs
// (doc-283/doc-292), or will it silently fall back to the old prompt-only path?
//
// doc-320: the fallback is correct and loud — in the BACKEND LOG. Nobody
// authoring a schema ever saw it, so an 18-field schema quietly forfeited the
// guaranteed output shape plus the measured win (QA doc-287: -34% output
// tokens, -25% latency) while its author believed structured outputs was on.
// This is the editor's mirror of that decision, evaluated live on the draft.
//
// MIRRORS backend/services/extraction/structured-json-schema.js
// buildExtractionJsonSchema — keep the two in step:
//   - a free-form array/object (a container with no itemFields) cannot be
//     closed under additionalProperties:false -> 'free_form_field'
//   - otherwise, every property whose compiled type is a UNION counts against
//     the provider's cap -> 'union_limit'. A container WITH itemFields still
//     counts (it compiles to ['array','null'] / ['object','null']); an enum
//     with options does NOT (it compiles to `{ enum: [...] }`, no `type` key);
//     an enum with no options degenerates to a nullable string and does.
// The cap itself is never hardcoded here — it comes from the meta-schema
// payload, so a provider change is a backend constant bump, not a frontend
// release. Returns null when it cannot be determined (no cap published, or
// nothing to judge yet).
export function constrainedOutputStatus(parsed, metaDoc) {
  const max = constraintsFrom(metaDoc).maxUnionProperties;
  if (max === null) return null;
  const fields = Array.isArray(parsed?.fields) ? parsed.fields : [];
  if (!fields.length) return null;

  const freeForm = [];
  const unionKeys = [];
  for (const f of fields) {
    // The compiler skips a keyless field outright, so it can't affect either count.
    if (!f || typeof f !== 'object' || Array.isArray(f)) continue;
    const key = typeof f.key === 'string' ? f.key.trim() : '';
    if (!key) continue;
    const type = f.type === undefined ? 'string' : f.type;
    if (isContainer(type)) {
      if (Array.isArray(f.itemFields) && f.itemFields.length) unionKeys.push(key);
      else freeForm.push(key);
      continue;
    }
    const hasOptions = type === 'enum'
      && Array.isArray(f.options) && f.options.filter((o) => o !== null).length > 0;
    if (!hasOptions) unionKeys.push(key);
  }

  if (freeForm.length) {
    return { constrained: false, reason: 'free_form_field', fields: freeForm, unionCount: unionKeys.length, max };
  }
  if (unionKeys.length > max) {
    return { constrained: false, reason: 'union_limit', fields: unionKeys, unionCount: unionKeys.length, max };
  }
  return { constrained: true, reason: null, fields: [], unionCount: unionKeys.length, max };
}

// Non-blocking advisory findings — the things that degrade extraction
// quality without failing a save. The no-item-fields rows carry the AI
// backfill action in the editor.
export function semanticWarnings(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  const warnings = [];
  const fields = Array.isArray(parsed.fields) ? parsed.fields : [];

  fields.forEach((f, i) => {
    if (!f || typeof f !== 'object') return;
    const name = typeof f.key === 'string' && f.key ? `"${f.key}"` : `#${i + 1}`;
    const path = ['fields', i];
    if (isContainer(f.type) && !(Array.isArray(f.itemFields) && f.itemFields.length)) {
      warnings.push({
        kind: 'no-item-fields',
        path,
        fieldIndex: i,
        fieldKey: typeof f.key === 'string' ? f.key : '',
        message: `${f.type === 'array' ? 'List' : 'Object'} field ${name} has no item fields — extraction leans on the prose description alone. Declare item fields for typed columns.`,
      });
    }
    if (!(typeof f.description === 'string' && f.description.trim())) {
      warnings.push({
        kind: 'empty-description',
        path,
        message: `Field ${name} has an empty description — that description is its extraction prompt.`,
      });
    }
    if (f.type === 'enum' && Array.isArray(f.options) && f.options.filter((o) => String(o).trim()).length === 1) {
      warnings.push({
        kind: 'single-option-enum',
        path,
        message: `Choice field ${name} has exactly one option — a choice needs alternatives.`,
      });
    }
    (Array.isArray(f.itemFields) ? f.itemFields : []).forEach((sf, j) => {
      if (sf && typeof sf === 'object' && !(typeof sf.description === 'string' && sf.description.trim())) {
        const itemName = typeof sf.key === 'string' && sf.key ? `"${sf.key}"` : `#${j + 1}`;
        warnings.push({
          kind: 'empty-description',
          path: [...path, 'itemFields', j],
          message: `Item field ${itemName} of ${name} has an empty description.`,
        });
      }
    });
  });

  // Tier-boundary convention (doc-59) — a suggestion, not a rule.
  const preamble = typeof parsed.promptPreamble === 'string' ? parsed.promptPreamble : '';
  if (/\b(tier|band)/i.test(preamble) && !/upper bound|\+\s*1|boundar/i.test(preamble)) {
    warnings.push({
      kind: 'tier-boundary',
      path: ['promptPreamble'],
      message: 'The preamble mentions tiers/bands but not their boundary semantics — consider stating them explicitly (e.g. \'open-ended tiers: min = the prior tier\'s upper bound + 1\') so "Above $X" isn\'t extracted as min = X.',
    });
  }

  return warnings;
}
